/**
 * API client for the ai-generate Edge Function.
 * Handles image enhancement, video generation, TTS, polling (video only), and token balance.
 *
 * Server-side polling: Image and TTS queue models are polled by the Edge Function.
 * Client-side polling: Video models still use client polling (jobs exceed Edge Function timeout).
 */

import { requireAuth, invokeEdgeFn } from './supabaseClient.ts';
import type { TokenBalance, CatalogModel } from '@/types/ai.ts';
import { env } from '@/config/environment';

const supabaseUrl = env.supabaseUrl;
const supabaseAnonKey = env.supabaseAnonKey;

// ─── Balance sync callback ──────────────────────────────────────────────

/** Sync balance from AI response (submit actions include updated balance) */
function syncBalance(data: any): void {
  if (data?.balance) {
    try {
      import('@/store/accountStore.ts').then(({ useAccountStore }) => {
        useAccountStore.getState().setAccountBalance({
          available: data.balance.available,
          used: data.balance.used,
          isBeta: data.balance.is_beta,
        });
      });
    } catch { /* store import can fail during SSR — ignore */ }
  }
}

/** Convenience: call ai-generate edge function with balance sync */
async function invokeAiFn(body: Record<string, unknown>): Promise<any> {
  return invokeEdgeFn('ai-generate', body, { onResponse: syncBalance });
}

// ─── Model catalog ──────────────────────────────────────────────────────

export async function fetchModelCatalog(): Promise<CatalogModel[]> {
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('Supabase not configured');

  const url = `${supabaseUrl}/functions/v1/ai-generate`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({ action: 'list-models' }),
  });
  if (!resp.ok) throw new Error(`Failed to fetch model catalog: ${resp.status}`);
  const data = await resp.json();
  return data.models as CatalogModel[];
}

// ─── Image enhancement (server-side polling — always returns final result) ──

export interface SubmitImageResult {
  jobId: string;
  tokenCost: number;
  resultDataUrl: string;
  seed: number | null;
}

/**
 * The debug harness's stand-in for a real image generation — see `src/debug/sceneBoardFakeAi.ts`,
 * which is the only thing that ever installs one.
 */
export type DebugFakeGeneration = (req: {
  imageDataUrl: string;
  prompt: string;
  modelId: string;
}) => Promise<SubmitImageResult>;

export async function submitImageEnhancement(
  imageDataUrl: string,
  prompt: string,
  styleRefUrl?: string,
  modelId = 'seedream',
): Promise<SubmitImageResult> {
  /**
   * HARNESS-ONLY SEAM, and deliberately the FIRST statement in the function.
   *
   * `?page=debug&view=scene-board` needs to show the Sketch-with-AI prompt panel, its loading
   * sequence and its review state without spending anything. Everything that would cost real money
   * or real state lives at or below this line — `requireAuth()`, the `ai-generate` invocation, and
   * server-side the token debit and the `ai_jobs` row — so returning here means: no auth, no fetch,
   * no credit debit, no job row.
   *
   * Everything ABOVE it in the call chain stays real, which is the whole point of putting the seam
   * here rather than in the orchestrator: `persistImageHistory`, the `ImageReviewItem` the review
   * renders, `handleApply` and `applyBatchGenerations` all run exactly as they do in production, so
   * the tile that lights up at the end is painted by the shipping code path.
   *
   * DEV-gated at the module level AND by the presence of the hook, which nothing but the harness
   * installs; a production bundle drops the branch entirely.
   */
  if (import.meta.env.DEV) {
    const fake = (window as unknown as { __debugFakeGeneration?: DebugFakeGeneration })
      .__debugFakeGeneration;
    if (typeof fake === 'function') return fake({ imageDataUrl, prompt, modelId });
  }
  await requireAuth();
  const data = await invokeAiFn({
    action: 'submit-image',
    image_data_url: imageDataUrl,
    prompt,
    style_reference_url: styleRefUrl,
    model: modelId,
  });
  if (data.status === 'failed' || (!data.result_data_url && data.error)) {
    throw new Error(data.error || 'Image enhancement failed');
  }
  return {
    jobId: data.job_id,
    tokenCost: data.token_cost,
    resultDataUrl: data.result_data_url,
    seed: data.seed ?? null,
  };
}

export async function requestRefund(amount: number): Promise<void> {
  await requireAuth();
  await invokeAiFn({ action: 'refund', amount });
}

export async function getBalance(): Promise<TokenBalance> {
  await requireAuth();
  const data = await invokeAiFn({ action: 'get-balance' });
  return {
    available: data.available,
    used: data.used,
    isBeta: data.is_beta,
  };
}

// ─── Video generation (client-side polling — jobs too long for server) ────

export interface SubmitVideoResult {
  jobId: string;
  statusUrl?: string;
  responseUrl?: string;
  tokenCost: number;
  /** Present when sync mode returns result synchronously */
  resultVideoUrl?: string;
  seed?: number | null;
}

export interface PollVideoResult {
  status: 'queued' | 'in_progress' | 'completed' | 'failed';
  resultVideoUrl?: string;
  error?: string;
  debug?: string;
  seed?: number | null;
}

export async function submitVideoGeneration(
  imageDataUrl: string,
  prompt: string,
  duration: number,
  resolution: string,
  model: string,
  generateAudio: boolean,
): Promise<SubmitVideoResult> {
  await requireAuth();
  const data = await invokeAiFn({
    action: 'submit-video',
    image_data_url: imageDataUrl,
    prompt,
    duration,
    resolution,
    model,
    generate_audio: generateAudio,
  });
  return {
    jobId: data.job_id,
    statusUrl: data.status_url,
    responseUrl: data.response_url,
    tokenCost: data.token_cost,
    resultVideoUrl: data.result_video_url,
    seed: data.seed ?? null,
  };
}

export async function pollVideoJob(statusUrl: string, responseUrl: string): Promise<PollVideoResult> {
  await requireAuth();
  const data = await invokeAiFn({
    action: 'poll',
    status_url: statusUrl,
    response_url: responseUrl,
  });
  return {
    status: data.status,
    resultVideoUrl: data.result_video_url,
    error: data.error,
    debug: data.debug,
    seed: data.seed ?? null,
  };
}

// ─── Transition video (first-last-frame, client-side polling) ────────────

export async function submitTransitionVideo(
  startImageDataUrl: string,
  endImageDataUrl: string,
  prompt: string,
  duration: number,
  resolution: string,
  model: string,
): Promise<SubmitVideoResult> {
  await requireAuth();
  const data = await invokeAiFn({
    action: 'submit-video-flf',
    start_image_data_url: startImageDataUrl,
    end_image_data_url: endImageDataUrl,
    prompt,
    duration,
    resolution,
    model,
  });
  return {
    jobId: data.job_id,
    statusUrl: data.status_url,
    responseUrl: data.response_url,
    tokenCost: data.token_cost,
    resultVideoUrl: data.result_video_url,
    seed: data.seed ?? null,
  };
}

/** Poll a video job until done. 5s interval, 5 min max.
 *  If the server says "completed but could not be retrieved", retry up to 3 more
 *  times with 10s gaps — the fal.ai result URL may just need time to propagate. */
export async function pollVideoUntilDone(
  statusUrl: string,
  responseUrl: string,
  onProgress?: (status: string) => void,
  abortSignal?: AbortSignal,
): Promise<PollVideoResult> {
  const POLL_INTERVAL = 5000;
  const MAX_POLLS = 60;
  const RETRIEVAL_RETRIES = 3;
  const RETRIEVAL_RETRY_DELAY = 10_000;

  let retrievalRetries = 0;

  for (let i = 0; i < MAX_POLLS; i++) {
    if (abortSignal?.aborted) throw new Error('Video generation cancelled');

    const result = await pollVideoJob(statusUrl, responseUrl);
    onProgress?.(result.status);

    if (result.status === 'completed') {
      return result;
    }

    if (result.status === 'failed') {
      // If the job completed but the server couldn't fetch the result, retry —
      // the CDN / response URL may just need more time to become available.
      const isRetrievalFailure = result.error?.includes('could not be retrieved');
      if (isRetrievalFailure && retrievalRetries < RETRIEVAL_RETRIES) {
        retrievalRetries++;
        console.warn(`Video result retrieval failed, retry ${retrievalRetries}/${RETRIEVAL_RETRIES}...`);
        onProgress?.('in_progress');
        await new Promise(r => setTimeout(r, RETRIEVAL_RETRY_DELAY));
        continue;
      }
      return result;
    }

    await new Promise(r => setTimeout(r, POLL_INTERVAL));
  }

  throw new Error('Video generation timed out');
}

// ─── TTS generation (server-side polling — always returns final result) ──

export interface SubmitTtsResult {
  jobId: string;
  tokenCost: number;
  resultAudioUrl: string;
  seed: number | null;
}

export interface SubmitTtsSceneResult {
  results: Array<{
    character: string;
    text: string;
    audioUrl?: string;
    error?: string;
  }>;
  tokenCost: number;
}

export async function submitTts(
  text: string,
  voice: string,
  model: string,
): Promise<SubmitTtsResult> {
  await requireAuth();
  const data = await invokeAiFn({
    action: 'submit-tts',
    text,
    voice,
    model,
  });
  if (data.status === 'failed' || (!data.result_audio_url && data.error)) {
    throw new Error(data.error || 'TTS generation failed');
  }
  return {
    jobId: data.job_id,
    tokenCost: data.token_cost,
    resultAudioUrl: data.result_audio_url,
    seed: data.seed ?? null,
  };
}

export async function submitTtsScene(
  lines: Array<{ text: string; voice: string; character: string }>,
  model: string,
): Promise<SubmitTtsSceneResult> {
  await requireAuth();
  const data = await invokeAiFn({
    action: 'submit-tts-scene',
    lines,
    model,
  });
  return {
    results: (data.results ?? []).map((r: any) => ({
      character: r.character,
      text: r.text,
      audioUrl: r.audio_url,
      error: r.error,
    })),
    tokenCost: data.token_cost,
  };
}

// ─── Audio generation (SFX sync, Music queue via ElevenLabs) ──────────────

export interface SubmitAudioGenResult {
  jobId: string;
  tokenCost: number;
  /** Present when sync (SFX) — result is immediate */
  resultAudioUrl?: string;
  seed?: number | null;
  /** Present when queued (Music) — client must poll */
  statusUrl?: string;
  responseUrl?: string;
}

export async function submitAudioGen(
  prompt: string,
  model: 'music' | 'sfx',
  duration?: number,
): Promise<SubmitAudioGenResult> {
  await requireAuth();
  const body: Record<string, unknown> = { action: 'submit-audio-gen', prompt, model };
  if (duration != null) body.duration = duration;
  const data = await invokeAiFn(body);
  if (data.error) {
    throw new Error(data.error);
  }
  return {
    jobId: data.job_id,
    tokenCost: data.token_cost,
    resultAudioUrl: data.result_audio_url,
    seed: data.seed ?? null,
    statusUrl: data.status_url,
    responseUrl: data.response_url,
  };
}

/** Poll a Music queue job until done. Uses dedicated poll-audio-gen action. */
export async function pollAudioGenUntilDone(
  statusUrl: string,
  responseUrl: string,
  onProgress?: (status: string) => void,
  abortSignal?: AbortSignal,
): Promise<{ resultAudioUrl: string; seed: number | null }> {
  const POLL_INTERVAL = 5000;
  const MAX_POLLS = 60; // 5 min max

  for (let i = 0; i < MAX_POLLS; i++) {
    if (abortSignal?.aborted) throw new Error('Audio generation cancelled');

    await new Promise(r => setTimeout(r, POLL_INTERVAL));

    const data = await invokeAiFn({
      action: 'poll-audio-gen',
      status_url: statusUrl,
      response_url: responseUrl,
    });

    onProgress?.(data.status);

    if (data.status === 'completed') {
      if (!data.result_audio_url) throw new Error('No audio URL in completed result');
      return { resultAudioUrl: data.result_audio_url, seed: data.seed ?? null };
    }

    if (data.status === 'failed') {
      throw new Error(data.error || 'Audio generation failed');
    }
  }

  throw new Error('Audio generation timed out');
}

// ─── Character-dossier inference (screenplay-import gap-fill) ────────────

/** Free LLM gap-fill for character appearance dossiers. Silent-degrades to [] on ANY failure
 *  (no session, network, server) — callers treat the heuristic dossier as the floor. */
export async function inferCharacterDossiers(
  characters: { name: string; context: string }[],
): Promise<{ name: string; appearance: string }[]> {
  if (characters.length === 0) return [];
  try {
    const data = await invokeAiFn({ action: 'infer-characters', characters });
    return Array.isArray((data as { results?: unknown }).results)
      ? (data as { results: { name: string; appearance: string }[] }).results
      : [];
  } catch {
    return [];
  }
}

/** Batch TTS for queue models (ElevenLabs). Server submits + polls all in parallel. */
export async function submitTtsBatch(
  lines: Array<{ text: string; voice: string; character: string }>,
  model: string,
): Promise<SubmitTtsSceneResult> {
  await requireAuth();
  const data = await invokeAiFn({
    action: 'submit-tts-batch',
    lines,
    model,
  });
  return {
    results: (data.results ?? []).map((r: any) => ({
      character: r.character,
      text: r.text,
      audioUrl: r.audio_url,
      error: r.error,
    })),
    tokenCost: data.token_cost,
  };
}
